import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'

/**
 * PrimeVue theme preset for LockerServer.
 *
 * Maps PrimeVue's semantic tokens (primary, surface, formField, text,
 * content, highlight) to the `--theme-*` CSS custom properties emitted
 * by `assets/styles/main.scss`. The SASS file is the single source of
 * truth for color, surface tone, typography, motion, and radii — edit
 * `_tokens.scss` and PrimeVue components, Tailwind utilities, and raw
 * CSS all update in lockstep.
 *
 * Per-component overrides go in the `components: {...}` block. Keep
 * those minimal — Aura defaults are good. Override only when design
 * system intent diverges.
 */
export const ThemePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: 'var(--theme-accent-50)',
      100: 'var(--theme-accent-100)',
      200: 'var(--theme-accent-200)',
      300: 'var(--theme-accent-300)',
      400: 'var(--theme-accent-400)',
      500: 'var(--theme-accent-500)',
      600: 'var(--theme-accent-600)',
      700: 'var(--theme-accent-700)',
      800: 'var(--theme-accent-800)',
      900: 'var(--theme-accent-900)',
      950: 'var(--theme-accent-950)',
    },
    borderRadius: {
      none: 'var(--theme-radius-none)',
      xs: 'var(--theme-radius-xs)',
      sm: 'var(--theme-radius-sm)',
      md: 'var(--theme-radius-md)',
      lg: 'var(--theme-radius-lg)',
      xl: 'var(--theme-radius-xl)',
    },
    focusRing: {
      width: 'var(--theme-focus-width)',
      style: 'solid',
      color: 'var(--theme-focus-color)',
      offset: 'var(--theme-focus-offset)',
      shadow: 'none',
    },
    // Form-control density — drives EVERY form field AND Button (both
    // default to `{form.field.padding.*}`): compact ~30px controls per
    // the reference. Shared sizing; colors stay per-mode in colorScheme.
    formField: {
      paddingX: 'var(--theme-space-3)',
      paddingY: 'var(--theme-space-1)',
      borderRadius: 'var(--theme-radius-md)',
    },
    colorScheme: {
      light: {
        surface: {
          0: 'var(--theme-neutral-0)',
          50: 'var(--theme-neutral-50)',
          100: 'var(--theme-neutral-100)',
          200: 'var(--theme-neutral-200)',
          300: 'var(--theme-neutral-300)',
          400: 'var(--theme-neutral-400)',
          500: 'var(--theme-neutral-500)',
          600: 'var(--theme-neutral-600)',
          700: 'var(--theme-neutral-700)',
          800: 'var(--theme-neutral-800)',
          900: 'var(--theme-neutral-900)',
          950: 'var(--theme-neutral-1000)',
        },
        primary: {
          color: 'var(--theme-accent)',
          contrastColor: 'var(--theme-text-on-accent)',
          hoverColor: 'var(--theme-accent-hover)',
          activeColor: 'var(--theme-accent-active)',
        },
        // Selected-item highlight (Select / MultiSelect / Listbox /
        // AutoComplete dropdown rows). Background is a translucent
        // accent tint so the underlying surface still shows through;
        // text resolves to readable primary on light surfaces.
        // Light: subtle 12% tint, brand-color text — readable on
        // white overlay bg.
        highlight: {
          background: 'color-mix(in srgb, var(--theme-accent) 12%, transparent)',
          focusBackground: 'color-mix(in srgb, var(--theme-accent) 20%, transparent)',
          color: 'var(--theme-accent-700)',
          focusColor: 'var(--theme-accent-800)',
        },
        formField: {
          // Form controls use `--theme-canvas` — the same tone the
          // sidebar uses. Article wrapper is `bg-surface` (lifted
          // tier); canvas is the BASE tier underneath. Form fields
          // sitting at canvas tone read as "sunken into" the article
          // — clear visual distinction against the surrounding
          // article bg without needing translucency tricks.
          //
          // Earlier attempts used semi-translucent `surface-elevated`
          // overlays, but those mixed too closely with `bg-surface`
          // (the article bg) to read as discrete controls. Solid
          // canvas tone is the cleanest way to get the "form field
          // is a different surface than its container" affordance.
          background: 'var(--theme-canvas)',
          disabledBackground: 'var(--theme-surface-hover)',
          filledBackground: 'var(--theme-canvas)',
          filledFocusBackground: 'var(--theme-surface-active)',
          borderColor: 'var(--theme-border)',
          hoverBorderColor: 'var(--theme-border-strong)',
          focusBorderColor: 'var(--theme-accent)',
          invalidBorderColor: 'var(--theme-status-danger)',
          color: 'var(--theme-text-primary)',
          disabledColor: 'var(--theme-text-disabled)',
          placeholderColor: 'var(--theme-text-tertiary)',
        },
        text: {
          color: 'var(--theme-text-primary)',
          hoverColor: 'var(--theme-neutral-900)',
          mutedColor: 'var(--theme-text-secondary)',
          hoverMutedColor: 'var(--theme-text-primary)',
        },
        content: {
          background: 'var(--theme-surface-elevated)',
          hoverBackground: 'var(--theme-surface-hover)',
          borderColor: 'var(--theme-border)',
          color: 'var(--theme-text-primary)',
        },
      },
      dark: {
        // ── Surface scale (mapped to Aura's monotone-lightness contract) ──
        //
        // Aura's component defaults reference `{surface.N}` assuming
        // a MONOTONE-LIGHTNESS scale: `surface.0` is the LIGHTEST in
        // every mode, `surface.950` the DARKEST. This is the SINGLE
        // unified contract every Aura component uses internally
        // (overlays, datatable stripes, toast detail color, list
        // option hover, formField bg, etc.).
        //
        // Our app-side `--theme-neutral-*` ramp is mode-INVERTED by
        // intent (`neutral-0` = canvas in BOTH modes — a SEMANTIC
        // scale, not a lightness scale). Feeding that to Aura
        // directly inverts every component that resolves through
        // `{surface.N}` in dark.
        //
        // The translation seam IS HERE. We feed Aura monotone-
        // lightness in BOTH modes by mapping our neutral indices in
        // reverse for dark. Light stays direct (lightness already
        // monotone there).
        //
        //   Aura wants in DARK         →  we feed
        //   surface.0  (LIGHTEST)      →  neutral-1000 (#ffffff)
        //   surface.50                 →  neutral-900  (#f4f5f7)
        //   surface.100                →  neutral-800
        //   surface.200                →  neutral-700
        //   ...
        //   surface.900                →  neutral-50   (#131316)
        //   surface.950 (DARKEST)      →  neutral-0    (#08090a — canvas)
        //
        // Result: Aura's defaults resolve to the right tone in both
        // modes without per-component overrides. Our explicit
        // overrides below (text.color, content.background, etc.)
        // still win where we want OUR semantic tokens to drive
        // (text-primary mode-flips via the emitter; surface
        // resolution is delegated to Aura).
        //
        // App-side Tailwind utilities (`bg-surface-N`,
        // `text-surface-N`) consume `--p-surface-N` and inherit this
        // monotone contract — so `text-surface-900` is dark text in
        // both modes (as Aura's docs say). Use semantic theme
        // classes (`text-primary`, `bg-canvas`, …) for mode-aware
        // styling; reserve raw shade classes for cases where you
        // actually want a specific lightness band.
        surface: {
          0: 'var(--theme-neutral-1000)',
          50: 'var(--theme-neutral-900)',
          100: 'var(--theme-neutral-800)',
          200: 'var(--theme-neutral-700)',
          300: 'var(--theme-neutral-600)',
          400: 'var(--theme-neutral-500)',
          500: 'var(--theme-neutral-400)',
          600: 'var(--theme-neutral-300)',
          700: 'var(--theme-neutral-200)',
          800: 'var(--theme-neutral-150)',
          900: 'var(--theme-neutral-50)',
          950: 'var(--theme-neutral-0)',
        },
        primary: {
          color: 'var(--theme-accent)',
          contrastColor: 'var(--theme-text-on-accent)',
          hoverColor: 'var(--theme-accent-hover)',
          activeColor: 'var(--theme-accent-active)',
        },
        // Dark counterpart — translucent accent over the dark
        // overlay surface; text in theme primary (light in dark
        // mode) for unambiguous contrast against the underlying
        // dark surface tint. Previous (accent-900 bg + accent-200
        // text) collapsed both to dark values via our mode-inverted
        // accent ramp = invisible-on-invisible.
        highlight: {
          background: 'color-mix(in srgb, var(--theme-accent) 22%, transparent)',
          focusBackground: 'color-mix(in srgb, var(--theme-accent) 32%, transparent)',
          color: 'var(--theme-text-primary)',
          focusColor: 'var(--theme-text-primary)',
        },
        formField: {
          // See light-mode rationale. In dark, canvas is `#08090a`
          // (deepest tone, same as sidebar); article is `#131316`
          // (`bg-surface`). Form fields at canvas read as clearly
          // recessed from the article — operator scans them as
          // discrete inputs without needing borders or shadows to
          // earn that distinction.
          background: 'var(--theme-canvas)',
          disabledBackground: 'var(--theme-surface-hover)',
          filledBackground: 'var(--theme-canvas)',
          filledFocusBackground: 'var(--theme-surface-active)',
          borderColor: 'var(--theme-border)',
          hoverBorderColor: 'var(--theme-border-strong)',
          focusBorderColor: 'var(--theme-accent)',
          invalidBorderColor: 'var(--theme-status-danger)',
          color: 'var(--theme-text-primary)',
          disabledColor: 'var(--theme-text-disabled)',
          placeholderColor: 'var(--theme-text-tertiary)',
        },
        text: {
          color: 'var(--theme-text-primary)',
          hoverColor: 'var(--theme-neutral-1000)',
          mutedColor: 'var(--theme-text-secondary)',
          hoverMutedColor: 'var(--theme-text-primary)',
        },
        content: {
          background: 'var(--theme-surface-elevated)',
          hoverBackground: 'var(--theme-surface-hover)',
          borderColor: 'var(--theme-border)',
          color: 'var(--theme-text-primary)',
        },
      },
    },
  },
  components: {
    button: {
      paddingX: 'var(--theme-space-3)',
      paddingY: 'var(--theme-space-1)',
      borderRadius: 'var(--theme-radius-md)',
      transitionDuration: 'var(--theme-duration-quick)',
      fontWeight: 'var(--theme-weight-medium)',
      // ── Split FILLED-BG vs OUTLINED/TEXT-LABEL accent ────────────
      // PV's default uses `primary.color` for both the filled-button
      // BG AND the outlined-button text color. With our deep dark
      // accent (`#2e5162`), filled BG looks great but outlined text
      // is unreadable on dark surfaces (~2.5:1). Override the
      // outlined + text variants per mode to use `--theme-accent-text`
      // (lifted in dark, deepened in light) — same fjord-blue family,
      // tuned for surface-vs-text contrast separately from the BG.
      colorScheme: {
        light: {
          outlined: {
            primary: {
              color: 'var(--theme-accent-text)',
              borderColor: 'var(--theme-accent-text)',
              hoverBackground: 'color-mix(in srgb, var(--theme-accent-text) 8%, transparent)',
              activeBackground: 'color-mix(in srgb, var(--theme-accent-text) 16%, transparent)',
            },
          },
          text: {
            primary: {
              color: 'var(--theme-accent-text)',
              hoverBackground: 'color-mix(in srgb, var(--theme-accent-text) 8%, transparent)',
              activeBackground: 'color-mix(in srgb, var(--theme-accent-text) 16%, transparent)',
            },
          },
        },
        dark: {
          outlined: {
            primary: {
              color: 'var(--theme-accent-text)',
              borderColor: 'var(--theme-accent-text)',
              hoverBackground: 'color-mix(in srgb, var(--theme-accent-text) 12%, transparent)',
              activeBackground: 'color-mix(in srgb, var(--theme-accent-text) 20%, transparent)',
            },
          },
          text: {
            primary: {
              color: 'var(--theme-accent-text)',
              hoverBackground: 'color-mix(in srgb, var(--theme-accent-text) 12%, transparent)',
              activeBackground: 'color-mix(in srgb, var(--theme-accent-text) 20%, transparent)',
            },
          },
        },
      },
    },
    // ── Tabs — active indicator + label use `--theme-accent-text` ──
    // Same reasoning as the Button outlined override: the filled-BG
    // accent (`#2e5162` in dark) is too dark for an active tab label
    // on dark surface. Lift to the readable text-accent.
    tabs: {
      tab: {
        activeColor: 'var(--theme-accent-text)',
        activeBorderColor: 'var(--theme-accent-text)',
      },
    },
    // ── Container nesting policy: list-shells are transparent ────
    // The design system pins ONE elevation tier per surface: the
    // outer container (Card / Panel / our bordered `article`
    // wrapper) is the bg-bearing primitive. Every "list-shell"
    // INSIDE that container — DataView, DataTable, Paginator,
    // Toolbar — should NOT carry its own bg. PV's defaults give
    // each one a `content.background` tint, producing a visible
    // nested-bg layer over the outer wrapper that breaks the
    // single-elevation read.
    //
    // Override to transparent so the outer wrapper's surface shows
    // through. Cards / Panels / Dialogs intentionally KEEP their
    // own backgrounds because they ARE elevation primitives, not
    // content presenters that live inside one.
    //
    // **Add new entries here** when a new in-container PV component
    // surfaces with the same nested-bg issue. Don't fix per-call-
    // site (e.g. inline `:pt="{root: { class: '!bg-transparent' }}"`)
    // — that's per-control hackery; this nesting rule is a design
    // system invariant.
    // ── DataView container — transparent BG + no paginator divider ──
    // Token paths verified against
    // `node_modules/@primeuix/themes/dist/aura/dataview/index.mjs`:
    //   { root, header, content, footer, paginatorTop, paginatorBottom }
    //
    //   * `content.background` — kill the inner-wrapper bg layer that
    //     would otherwise stack on the article's `bg-surface`.
    //   * `paginatorBottom.borderWidth` — PV defaults to `'1px 0 0 0'`
    //     (top divider on the bottom paginator). With our transparent
    //     surfaces the divider reads as "the paginator is glued to
    //     the last row." Drop it; paginator's own padding (set under
    //     `paginator.root.padding` below) provides the visual gap.
    dataview: {
      background: 'transparent',
      borderColor: 'transparent',
      content: {
        background: 'transparent',
      },
      paginatorBottom: {
        borderWidth: '0',
      },
      paginatorTop: {
        borderWidth: '0',
      },
    },
    datatable: {
      background: 'transparent',
      header: {
        background: 'transparent',
      },
      row: {
        background: 'transparent',
      },
    },
    paginator: {
      // PV's paginator preset nests tokens under `root` (verified
      // against `node_modules/@primeuix/themes/dist/aura/paginator/index.mjs`).
      // Top-level `padding` / `background` here are NO-OPS — they
      // MUST be set under `root` to take effect.
      root: {
        background: 'transparent',
        // Breathing room — PV's default `0.5rem 1rem` collapses the
        // paginator against the preceding row. Lift to space-4 (=1rem)
        // vertically; matches the visual gap between toolbar and
        // content above.
        padding: 'var(--theme-space-4) var(--theme-space-3)',
      },
      // Per-mode override kept as belt-and-suspenders.
      colorScheme: {
        light: { root: { background: 'transparent' } },
        dark: { root: { background: 'transparent' } },
      },
    },
    toolbar: {
      background: 'transparent',
    },
    inputtext: {
      paddingX: 'var(--theme-space-3)',
      paddingY: 'var(--theme-space-1)',
      borderRadius: 'var(--theme-radius-md)',
      fontSize: 'var(--theme-text-base)',
    },
    // ── Card + Panel — glassy elevation primitives ───────────────
    // Both are "container with own bg" surfaces. Per the section-
    // wrapper design rule, they use a translucent `surface-elevated`
    // overlay (same recipe as the notification card) so the
    // underlying canvas tone shows through softly. The matching
    // `backdrop-filter: blur(...)` lives in `main.scss` on
    // `.p-card` / `.p-panel` (CSS, not a PV preset token).
    //
    // Translucency keeps the brand identity of "lifted card" while
    // letting the page composite breathe — gives the surface a glassy
    // character against the page canvas without making the card
    // visually disappear.
    card: {
      // Glass KEPT (translucent bg; main.scss adds the backdrop blur +
      // the 1px border). Reference conform: radius → md, flatter shadow,
      // tighter body padding. Tokens nest under `root` (Aura contract).
      root: {
        background: 'color-mix(in srgb, var(--theme-surface-elevated) 70%, transparent)',
        borderRadius: 'var(--theme-radius-md)',
        shadow: 'var(--theme-shadow-sm)',
      },
      body: {
        padding: 'var(--theme-space-4)',
      },
    },
    panel: {
      background: 'color-mix(in srgb, var(--theme-surface-elevated) 70%, transparent)',
      borderColor: 'var(--theme-border)',
      borderRadius: 'var(--theme-radius-lg)',
      header: {
        background: 'transparent',
        borderColor: 'var(--theme-border)',
      },
      content: {
        background: 'transparent',
      },
    },
    // ── ToggleButton — checked state DARKER than container ────────
    // KEPT after the dark.surface reversal. This is NOT a surface
    // misalignment fix — it's a design-preference override. Aura's
    // default has the checked state SLIGHTLY LIGHTER than the
    // container in both modes (pressed-state convention varies).
    // User-flagged 2026-05-14: ON should sit VISUALLY LOWER than
    // OFF, like a pressed switch. Pin to our semantic surface ramp
    // so OFF = surface-elevated (lifted off canvas) and ON = canvas
    // (sinks into the page) in BOTH modes consistently.
    // PV Aura's default Tag info/success/warn/danger backgrounds in
    // dark mode are `color-mix(in srgb, {color.500}, transparent 84%)`
    // — translucent over the page surface, which can read as "barely
    // there" when the Tag sits over a busy / pale backdrop (e.g. an
    // image preview card). Override info to be SOLID in both modes
    // so a "Default" / "Info" badge always reads clearly. Other
    // severities keep PV defaults (only info was flagged 2026-05-17
    // by operator review).
    //
    // Color choice: sky.500 background + sky.50 text in BOTH modes
    // — saturated enough to pop on light AND dark surfaces; near-
    // white text gives clean contrast on the sky.500 fill.
    tag: {
      // Pill shape + compact label (reference "pill"): full radius,
      // ~11px, semibold. Severity colors stay Aura's readable soft
      // light defaults — except info kept SOLID below (prior operator
      // fix; reference pills are all-soft — revisit if wanted).
      root: {
        borderRadius: 'var(--theme-radius-full)',
        fontSize: 'var(--theme-text-xs)',
        fontWeight: 'var(--theme-weight-semibold)',
      },
      colorScheme: {
        light: {
          info: {
            background: '{sky.500}',
            color: '{sky.50}',
          },
        },
        dark: {
          info: {
            background: '{sky.500}',
            color: '{sky.50}',
          },
        },
      },
    },
    togglebutton: {
      colorScheme: {
        light: {
          root: {
            background: 'var(--theme-surface-elevated)',
            checkedBackground: 'var(--theme-surface-elevated)',
            hoverBackground: 'var(--theme-surface-hover)',
            borderColor: 'var(--theme-border)',
            color: 'var(--theme-text-secondary)',
            hoverColor: 'var(--theme-text-primary)',
            checkedColor: 'var(--theme-text-primary)',
            checkedBorderColor: 'var(--theme-border)',
          },
          content: {
            checkedBackground: 'var(--theme-canvas)',
          },
        },
        dark: {
          root: {
            background: 'var(--theme-surface-elevated)',
            checkedBackground: 'var(--theme-surface-elevated)',
            hoverBackground: 'var(--theme-surface-hover)',
            borderColor: 'var(--theme-border)',
            color: 'var(--theme-text-secondary)',
            hoverColor: 'var(--theme-text-primary)',
            checkedColor: 'var(--theme-text-primary)',
            checkedBorderColor: 'var(--theme-border)',
          },
          content: {
            checkedBackground: 'var(--theme-canvas)',
          },
        },
      },
    },
  },
})
